import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';
import { useConfirm } from '../../Context/ConfirmContext';

const IntegrationContainer = styled.div`
  background: rgba(99, 102, 241, 0.05);
  border: none;
  border-radius: 12px;
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding: 24px;
  margin-bottom: 20px;
  backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  padding: 10px 18px;
  display: inline-block;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  letter-spacing: 0.3px;
`;

const IntegrationOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: ${props => props.isAvailable ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  background: ${props => props.isConnected 
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)' 
    : 'rgba(255, 255, 255, 0.02)'};
  border-color: ${props => props.isConnected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'};
  opacity: ${props => props.isAvailable ? 1 : 0.6};
  backdrop-filter: blur(8px);

  &:hover {
    background: ${props => props.isAvailable ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
    border-color: ${props => props.isAvailable ? 'rgba(99, 102, 241, 0.4)' : 'rgba(99, 102, 241, 0.2)'};
    transform: ${props => props.isAvailable ? 'translateY(-1px)' : 'none'};
  }
`;

const IntegrationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IntegrationLogo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${props => props.bgColor || '#4285F4'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
`;

const GoogleLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const IntegrationDetails = styled.div`
  flex: 1;
`;

const IntegrationName = styled.div`
  color: #FFFFFF;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
`;

const IntegrationDescription = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${props => props.isConnected ? '#34A853' : 'rgba(255, 255, 255, 0.5)'};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.isConnected ? '#34A853' : 'rgba(255, 255, 255, 0.3)'};
`;

const Button = styled.button`
  background: ${props => {
    if (props.variant === 'google') return 'linear-gradient(135deg, #4285F4 0%, #3367D6 100%)';
    if (props.variant === 'danger') return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    if (props.variant === 'primary') return 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
    return 'rgba(99, 102, 241, 0.15)';
  }};
  color: #fff;
  border: ${props => props.variant ? 'none' : '1px solid rgba(99, 102, 241, 0.3)'};
  border-radius: 8px;
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: ${props => props.variant ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'};
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${props => props.variant ? '0 6px 20px rgba(99, 102, 241, 0.4)' : 'none'};
    background: ${props => !props.variant ? 'rgba(99, 102, 241, 0.25)' : null};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ConnectionSummary = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 12px;
  padding: 18px;
  margin-top: 16px;
  backdrop-filter: blur(8px);
`;

const SummaryTitle = styled.div`
  color: #fff;
  font-weight: 600;
  margin-bottom: 10px;
  font-size: 15px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  margin-bottom: 6px;
`;

const CalendarSelect = styled.div`
  margin-top: 16px;
  padding: 18px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%);
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.25);
  backdrop-filter: blur(8px);
`;

const SelectLabel = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  padding: 12px 14px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  option {
    background: #1a1a2e;
    color: #fff;
    padding: 8px;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #FFFFFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(234, 67, 53, 0.1);
  border: 1px solid rgba(234, 67, 53, 0.3);
  border-radius: 8px;
  padding: 12px;
  color: #EA4335;
  font-size: 14px;
  margin-top: 12px;
`;

// Calendar integration options
const CALENDAR_OPTIONS = [
  {
    id: 'google',
    name: 'Google Calendar',
    description: 'Sync your availability with Google Calendar',
    bgColor: '#4285F4',
    isAvailable: true
  },
  {
    id: 'outlook',
    name: 'Microsoft Outlook',
    description: 'Sync with Outlook Calendar (Coming Soon)',
    bgColor: '#0078D4',
    isAvailable: false
  },
  {
    id: 'apple',
    name: 'Apple Calendar',
    description: 'Sync with iCloud Calendar (Coming Soon)',
    bgColor: '#FF3B30',
    isAvailable: false
  }
];

const GoogleCalendarIntegration = () => {
  const apiClient = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  const [connectionStatus, setConnectionStatus] = useState({
    enabled: false,
    connected: false,
    selectedCalendarId: null,
    selectedCalendarName: null,
    lastSyncAt: null
  });
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Check for OAuth callback result in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('googleCalendarConnected') === 'true') {
      // Remove the query param from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh status
      fetchConnectionStatus();
    }
    if (params.get('googleCalendarError') === 'true') {
      setError('Failed to connect to Google Calendar. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchConnectionStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/google-calendar/status');
      setConnectionStatus(response.data);
      
      // If connected, fetch calendars
      if (response.data.connected) {
        fetchCalendars();
      }
    } catch (err) {
      console.error('Error fetching Google Calendar status:', err);
      // Don't show error for 404 (endpoint may not exist yet)
      if (err.response?.status !== 404) {
        setError('Failed to check Google Calendar connection status');
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  const fetchCalendars = async () => {
    try {
      setLoadingCalendars(true);
      const response = await apiClient.get('/google-calendar/calendars');
      setCalendars(response.data.calendars || []);
    } catch (err) {
      console.error('Error fetching calendars:', err);
      setError('Failed to fetch Google calendars');
    } finally {
      setLoadingCalendars(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      setConnectingGoogle(true);
      setError(null);
      
      const response = await apiClient.get('/google-calendar/auth');
      const { authUrl } = response.data;
      
      // Open Google OAuth in a new window/tab
      window.location.href = authUrl;
    } catch (err) {
      console.error('Error initiating Google auth:', err);
      setError('Failed to start Google Calendar connection. Please try again.');
      setConnectingGoogle(false);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = await confirm({
      title: 'Disconnect Google Calendar',
      message: 'Are you sure you want to disconnect Google Calendar? Your sync preferences will be removed.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (!confirmed) return;

    try {
      setLoading(true);
      await apiClient.post('/google-calendar/disconnect');
      setConnectionStatus({
        enabled: false,
        connected: false,
        selectedCalendarId: null,
        selectedCalendarName: null,
        lastSyncAt: null
      });
      setCalendars([]);
    } catch (err) {
      console.error('Error disconnecting Google Calendar:', err);
      setError('Failed to disconnect Google Calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleCalendarSelect = async (e) => {
    const selectedId = e.target.value;
    const selectedCal = calendars.find(cal => cal.id === selectedId);
    
    if (!selectedCal) return;

    try {
      setLoading(true);
      await apiClient.post('/google-calendar/select-calendar', {
        calendarId: selectedCal.id,
        calendarName: selectedCal.summary
      });
      
      setConnectionStatus(prev => ({
        ...prev,
        selectedCalendarId: selectedCal.id,
        selectedCalendarName: selectedCal.summary
      }));
    } catch (err) {
      console.error('Error selecting calendar:', err);
      setError('Failed to select calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      setError(null);
      
      const response = await apiClient.post('/google-calendar/sync-now');
      
      setSyncResult(response.data);
      
      // Refresh connection status to get updated lastSyncAt
      fetchConnectionStatus();
      
      // Clear sync result after 5 seconds
      setTimeout(() => setSyncResult(null), 5000);
    } catch (err) {
      console.error('Error syncing to Google Calendar:', err);
      setError('Failed to sync with Google Calendar. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <IntegrationContainer>
      <SectionTitle>Calendar Integrations</SectionTitle>

      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '16px' }}>
          Connect your calendar to automatically sync availability for meeting bookings.
        </p>
      </div>

      {error && (
        <ErrorMessage>
          ⚠️ {error}
          <Button
            style={{ marginLeft: '12px', padding: '4px 8px', fontSize: '12px' }}
            onClick={() => setError(null)}
          >
            Dismiss
          </Button>
        </ErrorMessage>
      )}

      {/* Calendar Options */}
      <div>
        {CALENDAR_OPTIONS.map(option => (
          <IntegrationOption 
            key={option.id}
            isAvailable={option.isAvailable}
            isConnected={option.id === 'google' && connectionStatus.connected}
            onClick={() => {
              if (option.id === 'google' && option.isAvailable && !connectionStatus.connected) {
                handleConnectGoogle();
              }
            }}
          >
            <IntegrationInfo>
              <IntegrationLogo bgColor={option.bgColor}>
                {option.id === 'google' ? <GoogleLogo /> : option.name.charAt(0)}
              </IntegrationLogo>
              <IntegrationDetails>
                <IntegrationName>{option.name}</IntegrationName>
                <IntegrationDescription>{option.description}</IntegrationDescription>
              </IntegrationDetails>
            </IntegrationInfo>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StatusBadge isConnected={option.id === 'google' && connectionStatus.connected}>
                <StatusDot isConnected={option.id === 'google' && connectionStatus.connected} />
                {option.id === 'google' && connectionStatus.connected 
                  ? 'Connected' 
                  : option.isAvailable 
                    ? 'Available' 
                    : 'Coming Soon'}
              </StatusBadge>
              
              {option.isAvailable && option.id === 'google' && (
                <div>
                  {connectionStatus.connected ? (
                    <Button 
                      variant="danger" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDisconnect();
                      }}
                      disabled={loading}
                    >
                      {loading ? <LoadingSpinner /> : 'Disconnect'}
                    </Button>
                  ) : (
                    <Button 
                      variant="google"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConnectGoogle();
                      }}
                      disabled={connectingGoogle}
                    >
                      {connectingGoogle ? <LoadingSpinner /> : (
                        <>
                          <GoogleLogo />
                          Connect
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </IntegrationOption>
        ))}
      </div>

      {/* Calendar Selection (when connected) */}
      {connectionStatus.connected && (
        <CalendarSelect>
          <SelectLabel>Select calendar to sync with:</SelectLabel>
          {loadingCalendars ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.7)' }}>
              <LoadingSpinner />
              Loading calendars...
            </div>
          ) : (
            <Select 
              value={connectionStatus.selectedCalendarId || ''} 
              onChange={handleCalendarSelect}
            >
              <option value="">-- Select a calendar --</option>
              {calendars.map(cal => (
                <option key={cal.id} value={cal.id}>
                  {cal.summary} {cal.primary ? '(Primary)' : ''}
                </option>
              ))}
            </Select>
          )}
        </CalendarSelect>
      )}

      {/* Connection Summary */}
      {connectionStatus.connected && connectionStatus.selectedCalendarId && (
        <ConnectionSummary>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <SummaryTitle>Google Calendar Connected</SummaryTitle>
              <SummaryItem>
                <span>Calendar:</span>
                <span>{connectionStatus.selectedCalendarName}</span>
              </SummaryItem>
              {connectionStatus.lastSyncAt && (
                <SummaryItem>
                  <span>Last synced:</span>
                  <span>{new Date(connectionStatus.lastSyncAt).toLocaleString()}</span>
                </SummaryItem>
              )}
            </div>
            <Button 
              variant="primary"
              onClick={handleSyncNow}
              disabled={syncing}
              style={{ minWidth: '120px' }}
            >
              {syncing ? (
                <>
                  <LoadingSpinner />
                  Syncing...
                </>
              ) : (
                '🔄 Sync Now'
              )}
            </Button>
          </div>
          
          {/* Sync Result Message */}
          {syncResult && (
            <div style={{ 
              marginTop: '12px', 
              padding: '10px', 
              backgroundColor: syncResult.success ? 'rgba(52, 168, 83, 0.1)' : 'rgba(234, 67, 53, 0.1)',
              border: `1px solid ${syncResult.success ? 'rgba(52, 168, 83, 0.3)' : 'rgba(234, 67, 53, 0.3)'}`,
              borderRadius: '6px',
              color: syncResult.success ? '#34A853' : '#EA4335',
              fontSize: '14px'
            }}>
              {syncResult.success ? '✅' : '❌'} {syncResult.message}
              {syncResult.synced > 0 && (
                <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                  ({syncResult.synced} meeting{syncResult.synced !== 1 ? 's' : ''} synced)
                </span>
              )}
            </div>
          )}
          
          <div style={{ marginTop: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
            ✓ Your availability will be checked against this calendar<br/>
            ✓ New bookings will be added to this calendar automatically<br/>
            ✓ Auto-sync runs every 5 minutes
          </div>
        </ConnectionSummary>
      )}

      {/* Info when connected but no calendar selected */}
      {connectionStatus.connected && !connectionStatus.selectedCalendarId && (
        <div style={{ 
          marginTop: '12px', 
          padding: '12px', 
          backgroundColor: 'rgba(251, 188, 5, 0.1)',
          border: '1px solid rgba(251, 188, 5, 0.3)',
          borderRadius: '8px',
          color: '#FBBC05',
          fontSize: '14px'
        }}>
          ⚠️ Please select a calendar to enable availability sync
        </div>
      )}
    </IntegrationContainer>
  );
};

export default GoogleCalendarIntegration;

